import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';

import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { mislecAPI } from '../../util/axios';

function Language({ user, updateUser }) {
    const { i18n, t } = useTranslation();
    const languages = i18n.options.supportedLngs.slice(0, -1);

    const [open, setOpen] = React.useState(null);
    const [currentLanguage, setCurrentLanguage] = React.useState(null);

    React.useEffect(() => {
        if (!currentLanguage) {
            if (user && user.language) {
                setCurrentLanguage(user.language);
                i18n.changeLanguage(user.language);
            } else {
                setCurrentLanguage(i18n.language);
            }
        }
    }, [currentLanguage]);

    const updateLanguage = (language) => {
        if (user) {
            //  Request body
            const data = {
                ...user,
                language: language
            };

            // Send data to server
            mislecAPI.put(`/api/users/language/${user._id}`, data)
                .then((res) => {
                    updateUser(res.data.user)
                })
                .catch(error => {
                    if (error.response?.data.detail === 'api.auth.verify.401') {
                        //  Expired session
                        updateUser(null);
                    }
                });
        }
    }

    const handleOpen = (event) => {
        setOpen(event.currentTarget);
    };

    const handleClose = () => {
        setOpen(null);
    };

    const handleLanguageChange = event => {
        i18n.changeLanguage(event.currentTarget.dataset.value);
        setCurrentLanguage(event.currentTarget.dataset.value);
        updateLanguage(event.currentTarget.dataset.value);
        setOpen(null);
    };

    return (
        <>
            {currentLanguage && (
                <>
                    <Tooltip title={t('header.changeLanguage')} arrow>
                        <IconButton
                            onClick={handleOpen}
                            sx={{
                                borderRadius: '8px',
                                '&:hover': {
                                    backgroundColor: 'rgba(145, 158, 171, 0.08)',
                                },
                            }}
                            disableFocusRipple
                            disableRipple
                        >
                            <ReactCountryFlag
                                countryCode={currentLanguage.split('-')[1]}
                                svg
                                style={{
                                    fontSize: '0.9em',
                                    textAlign: 'center',
                                    borderRadius: '8px',
                                    marginBottom: '3px'
                                }}
                            />
                            <Typography variant='body2' sx={{ ml: 1, color: 'text.primary', fontSize: '0.8rem' }}>
                                {t('header.language.' + currentLanguage)}
                            </Typography>
                            {open ? <ExpandLessIcon sx={{ width: '18px', ml: 0.5 }} /> : <ExpandMoreIcon sx={{ width: '18px', ml: 0.5 }} />}
                        </IconButton>
                    </Tooltip>
                    <Popover
                        open={!!open}
                        anchorEl={open}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                            sx: {
                                p: 0,
                                mt: 1,
                                ml: 0.75,
                                width: 150,
                            },
                        }}
                    >
                        {languages.map(
                            language =>
                                language !== currentLanguage && (
                                    <MenuItem
                                        data-value={language}
                                        value={language}
                                        key={language}
                                        onClick={handleLanguageChange}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            my: 0.5,
                                            px: 2,
                                            color: 'text.primary',
                                        }}
                                    >
                                        <ReactCountryFlag
                                            countryCode={language.split('-')[1]}
                                            svg
                                            style={{ fontSize: '1.2em', width: '1.2em', borderRadius: '4px' }}
                                        />

                                        <Typography variant='body2' sx={{ color: 'text.primary', fontSize: '0.8rem', ml: 1 }}>
                                            {t('header.language.' + language)}
                                        </Typography>
                                    </MenuItem>
                                )
                        )}
                    </Popover>
                </>

            )}
        </>
    );
}

export default Language;